import { Image, Modal } from 'antd';
import { UploadFile } from 'antd/es/upload/interface';
import { modalWrapperClassName } from 'components/Dialogue';
import ProxyVideo from 'components/ProxyVideo';
import useModal from 'hooks/useModal';
import { ProviderProps, useContext, useMemo, useState } from 'react';
import createNamedContext from 'utils/createNamedContext';

export type UploadContextValues = ReturnType<typeof useUploadValue>;

export const UploadContext = createNamedContext<UploadContextValues | null>('UploadContext', null);

export function useUploadContext() {
  const value = useContext(UploadContext);

  if (!value) {
    throw new Error(
      'useUploadContext hook should be used in a context of UploadProvider component',
    );
  }

  return value;
}

export function useUploadValue() {
  const [imageList, setImageList] = useState<UploadFile[]>([]);
  const [videoList, setVideoList] = useState<UploadFile[]>([]);

  const imageModal = useModal<string>();
  const videoModal = useModal<string>();

  return useMemo(
    () => ({
      imageList,
      videoList,
      imageModal,
      videoModal,
      setImageList,
      setVideoList,
    }),
    [imageList, videoList, imageModal, videoModal, setImageList, setVideoList],
  );
}

export function UploadProvider({ children, value }: ProviderProps<UploadContextValues>) {
  const { imageModal, videoModal } = value;

  const imagePreview = (
    <Image
      wrapperStyle={{ display: 'none' }}
      src={imageModal.meta}
      preview={{
        visible: imageModal.visible,
        onVisibleChange: (visible) => imageModal.update(visible),
        src: imageModal.meta,
      }}
    />
  );

  const videoPreview = (
    <Modal
      visible={videoModal.visible}
      onCancel={() => videoModal.hide()}
      title={null}
      footer={false}
      wrapClassName={modalWrapperClassName}
    >
      <ProxyVideo controls src={videoModal.meta} style={{ width: '100%' }}>
        <track kind="captions" />
      </ProxyVideo>
    </Modal>
  );

  return (
    <UploadContext.Provider value={value}>
      {children}
      {imagePreview}
      {videoPreview}
    </UploadContext.Provider>
  );
}
